"use client"

import React, { useMemo, useState } from 'react';
import { TrendingUp } from "lucide-react"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts" // Added YAxis import
import { format, subDays, startOfDay } from 'date-fns'; // For date manipulation
import { Transaction } from '@/types/transaction'; // Import Transaction type
import { mockTransactions } from '@/lib/mockData'; // Import mock data for default/fallback

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"; // Import ToggleGroup
import { cn } from "@/lib/utils"; // Import cn for styling
import { ScrollArea } from "@/components/ui/scroll-area"; // Import ScrollArea

interface SpendingChartProps {
  transactions: Transaction[];
  initialDays?: number; // Changed prop name for clarity
}

const DEFAULT_INITIAL_DAYS = 30;

export function SpendingChart({ transactions = mockTransactions, initialDays = DEFAULT_INITIAL_DAYS }: SpendingChartProps) {

  // State to manage the selected number of days
  const [selectedDays, setSelectedDays] = useState<number>(initialDays);

  const chartData = useMemo(() => {
    const endDate = startOfDay(new Date());
    const startDate = subDays(endDate, selectedDays - 1); // Use selectedDays state

    // Filter transactions within the date range and only include expenses
    const relevantTransactions = transactions.filter(tx => {
      try {
        const txDate = startOfDay(new Date(tx.date + 'T00:00:00Z')); // Treat date as UTC midnight
        // Ensure amount is negative (expense)
        return tx.amount < 0 && txDate >= startDate && txDate <= endDate;
      } catch (e) {
        console.error("Error parsing transaction date for chart:", tx.date, e);
        return false;
      }
    });

    // Aggregate expenses by day
    // Store both total expenses and the list of transactions for the tooltip
    const dailyData = new Map<string, { expenses: number; transactions: Transaction[] }>();

    // Initialize map with all dates in the range to ensure continuity
    for (let i = 0; i < selectedDays; i++) { // Use selectedDays state
        const date = subDays(endDate, i);
        const formattedDate = format(date, 'yyyy-MM-dd');
        dailyData.set(formattedDate, { expenses: 0, transactions: [] }); // Initialize with empty array
    }

    relevantTransactions.forEach(tx => {
      // Date already validated in filter step
      const formattedDate = format(startOfDay(new Date(tx.date + 'T00:00:00Z')), 'yyyy-MM-dd');
      const currentData = dailyData.get(formattedDate);
      if (currentData) {
        // Add to total expenses and push transaction to the list
        currentData.expenses += Math.abs(tx.amount);
        currentData.transactions.push(tx);
      } 
    });

    // Format for Recharts and sort by date
    const formattedData = Array.from(dailyData.entries())
        .map(([date, data]) => ({ date, expenses: data.expenses, transactions: data.transactions })) // Include transactions array
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return formattedData;
  }, [transactions, selectedDays]); // Depend on selectedDays state

  const chartConfig = useMemo(() => ({
    expenses: {
      label: "Expenses",
      color: "hsl(var(--chart-1))",
    },
  }), []);

  const totalExpenses = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.expenses, 0);
  }, [chartData]);

  // Custom Tooltip Component
  const CustomTooltipContent = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload; // Access the full data object
      const date = label; // Already formatted by XAxis
      const total = data.expenses;
      const transactions: Transaction[] = data.transactions || [];

      // Only show tooltip if there are expenses for the day
      if (total <= 0) {
        return null;
      }

      return (
        <Card className="w-64 shadow-lg text-xs">
          <CardHeader className="p-2 border-b">
            <p className="font-semibold text-sm">{format(new Date(date + 'T00:00:00Z'), 'PPP')}</p>
            <p className="text-muted-foreground">
              Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total)}
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {transactions.length > 0 ? (
              <ScrollArea className="h-32 p-2">
                <ul className="space-y-1">
                  {transactions.map((tx) => (
                    <li key={tx.id || tx.transaction_id} className="flex justify-between items-center gap-2 text-xs">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {tx.logo_url ? (
                          <img
                            src={tx.logo_url}
                            alt={`${tx.merchant_name || tx.name || 'Transaction'} logo`}
                            className="h-4 w-4 rounded-sm object-contain flex-shrink-0"
                            onError={(e) => (e.currentTarget.style.display = 'none')}
                          />
                        ) : (
                          <div className="h-4 w-4 rounded-sm bg-muted flex items-center justify-center text-[10px] text-muted-foreground flex-shrink-0">
                             {(tx.merchant_name || tx.name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="truncate">{tx.merchant_name || tx.name || 'N/A'}</span>
                      </div>
                      <span className={cn(
                        "font-medium whitespace-nowrap flex-shrink-0",
                        tx.amount > 0 ? 'text-green-600' : ''
                      )}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: tx.iso_currency_code || 'USD' }).format(tx.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            ) : (
              <p className="p-2 text-muted-foreground italic">No transactions on this day.</p>
            )}
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"> {/* Adjusted for layout */}
        <div> {/* Wrap title/desc */}
          <CardTitle>Spending Overview</CardTitle>
          <CardDescription>Total expenses over the last {selectedDays} days.</CardDescription> {/* Use selectedDays state */}
        </div>
        <ToggleGroup
            type="single"
            defaultValue={selectedDays.toString()} // Use state for default
            onValueChange={(value) => {
                if (value) setSelectedDays(parseInt(value)); // Update state on change
            }}
            size="sm"
            aria-label="Select chart time range"
        >
            <ToggleGroupItem value="7" aria-label="Last 7 days">7d</ToggleGroupItem>
            <ToggleGroupItem value="30" aria-label="Last 30 days">30d</ToggleGroupItem>
            <ToggleGroupItem value="90" aria-label="Last 90 days">90d</ToggleGroupItem>
        </ToggleGroup>
      </CardHeader>
      <CardContent className="pt-4"> {/* Added padding top to content */}
        <ChartContainer config={chartConfig} className="h-[250px] w-full">
          <BarChart
            accessibilityLayer // Good for accessibility
            data={chartData}
            margin={{ /* Keep margins */
              top: 5,
              right: 5,
              left: 10, // Increase left margin for padding
              bottom: 5,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8} // Adjusted tick margin
              tickFormatter={(value) => format(new Date(value + 'T00:00:00Z'), selectedDays <= 31 ? 'MMM d' : 'MMM yy')} // Use selectedDays state
              interval={selectedDays > 30 ? 'preserveStartEnd' : Math.max(0, Math.floor(selectedDays / 7) - 1 )} // Use selectedDays state
              padding={{ left: 10, right: 10 }} // Keep padding
              fontSize={12} // Keep font size
            />
             <YAxis // Add YAxis back for context
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => `$${value}`}
                width={50} // Keep width
                fontSize={12}
            />
            <ChartTooltip
              cursor={false} // Keep cursor disabled
              content={<CustomTooltipContent />} // Use the custom component
              wrapperStyle={{ zIndex: 999 }} // Keep z-index just in case
              isAnimationActive={false} // Keep animation disabled
            />
            <Bar dataKey="expenses" fill="var(--color-expenses)" radius={4} /> {/* Adjusted radius */}
          </BarChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm pt-4"> {/* Added padding top */}
        {/* Optional: Add dynamic trending later */}
        {/* <div className="flex gap-2 font-medium leading-none">
          Trending info here <TrendingUp className="h-4 w-4" />
        </div> */}
        <div className="leading-none text-muted-foreground">
          Total expenses shown: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalExpenses)}
        </div>
      </CardFooter>
    </Card>
  )
} 